<script setup lang="ts">
import type { CSSProperties } from 'vue'
import { aspectMaps, ratioMap } from 'nuxt-base/components/BaseImage.vue'
import type BaseImage from 'nuxt-base/components/BaseImage.vue'

const props = defineProps<{ media: gen.ImageTaggedFragment[] | None; title: string | None }>()
const mobile = useMobile()

const data = shallowReactive({
  index: 0,
  ready: false,
})

const elem = useTemplateRef('elem')
const scrolHelp = useTemplateRef('scrolHelp')

const timer = useTimeout()
const timer2 = useTimeout()
const timer3 = useTimeout()

const init = useInited('init2')

const ready = computed(() => data.ready && init.value)
const count = computed(() => props.media?.length ?? 0)
const empty = computed(() => !props.media?.at(0))

const images = computed((): CSSProperties | undefined => {
  const images = props.media
  if (!images?.length) return
  let res: undefined | { r: number; aspect: { x: number; y: number } }

  for (const item of images) {
    const aa = item.ratio ? ratioMap[item.ratio] : undefined
    const asp = aa ? aspectMaps[aa] : undefined
    const aspect = asp?.xy || item.img.image2?.aspect
    if (!aspect) continue
    const r = aspect.y / aspect.x
    if (!res?.r || r > res.r) res = { r, aspect }
  }

  return res?.aspect ? { aspectRatio: `${res?.aspect.x}/${res?.aspect.y}` } : undefined
})

watch(() => count.value, () => scrolHelp.value?.checkSize(), { immediate: true })

function scroll() {
  scrolHelp.value?.onScroll()
  timer.set(() => {
    const left = myElem(elem.value, 'scrollLeft', 0)
    const all = myElem(elem.value, 'offsetWidth', 0)
    if (!all) return
    const index = Math.round((left / all) - 0.4)
    if (data.index !== index) data.index = index
  }, 20)
}

function loaded() {
  timer2.set(() => (data.ready = true), 500)
}

const refs = useRefsMap<typeof BaseImage, string>()

onMounted(() => {
  const el = helpers.getHTMLElement(refs.get('a'))
  const img = el?.getElementsByTagName('img').item(0)
  const vid = el?.getElementsByTagName('video').item(0)
  const loaded2 = !vid?.paused
  const loaded = img && img.complete && img.naturalWidth !== 0 && img.naturalHeight !== 0
  timer3.set(() => data.ready = true, loaded || loaded2 ? 100 : 6000)
})
</script>

<template>
  <div class="_relative" :class="[!count && $style.off]">
    <div ref="elem" :class="$style.c1" :style="images" @scroll.passive="() => scroll()">
      <BaseImage v-if="empty" sizes="fixed:50" :class="$style.e0" />

      <template v-for="(item, index) in media" :key="index">
        <BaseImage
          v-if="index === 0 || init"
          :ref="index === 0 ? (el) => refs.set('a', el) : undefined"
          :img="item.img"
          :alt="index === 0 ? title : undefined"
          :ratio="item.ratio"
          :class="$style.img"
          :lazy="!!index"
          :on-load="index === 0 ? loaded : undefined"
          sizes="100vw"
          autoplay
        >
          <LazyProductImageTags v-if="ready" :tags="item.tags" />
        </BaseImage>
      </template>
    </div>

    <LazyUiScrollArrows v-if="!mobile && ready" ref="scrolHelp" :elem="() => elem" scroll-screen />
    <LazyProductImageCounter v-if="count && ready" :class="[$style.b1, $clsClass('img-counter3')]" :max="count" :index="data.index" />
  </div>
</template>

<style module>
.e0 {
  width: 100vw;
  composes: global(_hm100vh);
  composes: global(_a1);
}
.b1 {
  composes: global(_absolute);
  left: 20px;
  min-width: 50%;
  bottom: 10px;
  right: 0;
}
.off {
  min-height: 50vh
}
.c1 {
  composes: global(_row);
  composes: global(_scrollX);
  composes: global(_gap02);
  composes: global(_w100p);
  scroll-behavior: smooth;
  scroll-padding: 0;
  scroll-snap-type: x mandatory;
}
.img {
  composes: global(_w100p);
  composes: global(_noflex);
  scroll-snap-align: start
}
</style>
